import { ReactComponent as WarningStaticIcon } from '@basqet-dashboard/common/assets/icons/warning-static.svg';
import { Button, Modal } from '@basqet-dashboard/common/components';
import { FileUpload, PersonRow } from '@components/onboarding';
import { ANALYTICS_EVENTS, KYB_ACCOUNT_TYPE } from '@config';
import { useAnalytics, useTrackOnMount } from '@hooks';
import { useOwnershipSubmit } from '@hooks/useOwnershipSubmit';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { KYB_SAMPLE_DOCS } from '../../shared-constants';
import { DirectorPanel } from './panels/director-panel';
import { ShareholderPanel } from './panels/shareholder-panel';
import { UBOPanel } from './panels/ubo-panel';

const OwnershipStructure = ({ onComplete, initialData, onSaveDraft, readOnly = false }) => {
  const { track } = useAnalytics();

  useTrackOnMount(ANALYTICS_EVENTS.KYB_OWNERS_VIEWED, {
    kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI
  });

  const [directors, setDirectors] = useState(initialData?.directors || []);
  const [directorEntryIds, setDirectorEntryIds] = useState(initialData?._directorEntryIds || {});
  const [particularsFile, setParticularsFile] = useState(initialData?.particulars_file || null);
  const [particularsAttachmentId, setParticularsAttachmentId] = useState(
    initialData?.particulars_attachment_id || null
  );

  const [shareholders, setShareholders] = useState(initialData?.shareholders || []);
  const [shareholderEntryIds, setShareholderEntryIds] = useState(
    initialData?._shareholderEntryIds || {}
  );
  const [certifiedListFile, setCertifiedListFile] = useState(
    initialData?.certified_list_file || null
  );
  const [certifiedListAttachmentId, setCertifiedListAttachmentId] = useState(
    initialData?.certified_list_attachment_id || null
  );

  const [ubos, setUbos] = useState(initialData?.ubos || []);
  const [uboEntryIds, setUboEntryIds] = useState(initialData?._uboEntryIds || {});
  const [uboAttachmentIds, setUboAttachmentIds] = useState({});

  const [panel, setPanel] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileDeleteTarget, setFileDeleteTarget] = useState(null);
  const [showFileDeleteModal, setShowFileDeleteModal] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    saveDraft,
    handleSectionFileChange,
    handleDeleteFile,
    handleDeletePerson,
    isFormValid,
    handleSubmit
  } = useOwnershipSubmit({
    directors,
    shareholders,
    ubos,
    particularsFile,
    certifiedListFile,
    particularsAttachmentId,
    certifiedListAttachmentId,
    uboAttachmentIds,
    directorEntryIds,
    shareholderEntryIds,
    uboEntryIds,
    setParticularsFile,
    setParticularsAttachmentId,
    setCertifiedListFile,
    setCertifiedListAttachmentId,
    setDirectorEntryIds,
    setShareholderEntryIds,
    setUboEntryIds,
    setUboAttachmentIds,
    onSaveDraft
  });

  const openPanel = (type, entry = null) => setPanel({ type, entry });
  const closePanel = () => setPanel(null);

  const handlePanelSave = (data) => {
    const { type, entry } = panel;
    if (type === 'director') {
      const newDirectors = entry
        ? directors.map((d) => (d.id === entry.id ? { ...d, ...data } : d))
        : [...directors, { id: Date.now(), ...data }];
      if (!entry) {
        track(ANALYTICS_EVENTS.KYB_DIRECTOR_ADDED, {
          kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI,
          director_name: data.name
        });
      }
      setDirectors(newDirectors);
      saveDraft({ directors: newDirectors });
    } else if (type === 'shareholder') {
      const newShareholders = entry
        ? shareholders.map((s) => (s.id === entry.id ? { ...s, ...data } : s))
        : [...shareholders, { id: Date.now(), ...data }];
      if (!entry) {
        track(ANALYTICS_EVENTS.KYB_SHAREHOLDER_ADDED, {
          kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI,
          shareholder_name: data.name
        });
      }
      setShareholders(newShareholders);
      saveDraft({ shareholders: newShareholders });
    } else if (type === 'ubo') {
      const newUbos = entry
        ? ubos.map((u) => (u.id === entry.id ? { ...u, ...data } : u))
        : [...ubos, { id: Date.now(), ...data }];
      if (!entry) {
        track(ANALYTICS_EVENTS.KYB_UBO_ADDED, {
          kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI,
          ubo_name: data.name
        });
      }
      setUbos(newUbos);
      saveDraft({ ubos: newUbos });
    }
    closePanel();
  };

  const onDeletePerson = async () => {
    await handleDeletePerson(deleteTarget, { setDirectors, setShareholders, setUbos });
    setDeleteTarget(null);
    setShowDeleteModal(false);
  };

  const onDeleteFile = () => {
    handleDeleteFile(fileDeleteTarget);
    setFileDeleteTarget(null);
    setShowFileDeleteModal(false);
  };

  const onFormSubmit = async (e) => {
    e.preventDefault();
    const errs = await handleSubmit({ onComplete, setIsSubmitting });
    if (errs) setFieldErrors(errs);
  };

  return (
    <form className="corp-step-form" onSubmit={onFormSubmit}>
      {/* ── Business Directors ── */}
      <div className="corp-step-form__section-card">
        <h3 className="corp-step-form__section-title">Business Directors</h3>
        {!readOnly && (
          <button
            type="button"
            className="corp-step-form__add-btn"
            onClick={() => openPanel('director')}>
            + Add business director
          </button>
        )}

        {directors.length > 0 && (
          <div className="ownership-person-list">
            {directors.map((d) => (
              <PersonRow
                key={d.id}
                name={d.name}
                readOnly={readOnly}
                onEdit={() => openPanel('director', d)}
                onDelete={() => {
                  setDeleteTarget({ type: 'director', id: d.id });
                  setShowDeleteModal(true);
                }}
              />
            ))}
          </div>
        )}

        {fieldErrors.directors && (
          <span className="corp-step-form__error">{fieldErrors.directors}</span>
        )}

        <div className="corp-step-form__file-section">
          <h4 className="corp-step-form__file-section-title">Particulars of Directors</h4>
          <p className="corp-step-form__file-section-desc">
            Upload an official document that lists all directors; it must be issued or certified by
            the appropriate authority.
          </p>
          <FileUpload
            value={particularsFile}
            onChange={(file) => handleSectionFileChange('particulars', file)}
            onDelete={() => {
              setFileDeleteTarget('particulars');
              setShowFileDeleteModal(true);
            }}
            sampleTitle={{ normal: 'Particulars of', cursive: 'Directors' }}
            sampleImage={KYB_SAMPLE_DOCS.PARTICULARS_OF_DIRECTORS}
            maxSizeMB={5}
            error={fieldErrors.particulars_file}
            disabled={readOnly}
          />
        </div>
      </div>

      {/* ── Shareholders ── */}
      <div className="corp-step-form__section-card">
        <h3 className="corp-step-form__section-title">Shareholders</h3>
        {!readOnly && (
          <button
            type="button"
            className="corp-step-form__add-btn"
            onClick={() => openPanel('shareholder')}>
            + Add shareholder
          </button>
        )}

        {shareholders.length > 0 && (
          <div className="ownership-person-list">
            {shareholders.map((s) => (
              <PersonRow
                key={s.id}
                name={s.name}
                readOnly={readOnly}
                onEdit={() => openPanel('shareholder', s)}
                onDelete={() => {
                  setDeleteTarget({ type: 'shareholder', id: s.id });
                  setShowDeleteModal(true);
                }}
              />
            ))}
          </div>
        )}

        {fieldErrors.shareholders && (
          <span className="corp-step-form__error">{fieldErrors.shareholders}</span>
        )}

        <div className="corp-step-form__file-section">
          <h4 className="corp-step-form__file-section-title">Certified List of Shareholders</h4>
          <p className="corp-step-form__file-section-desc">
            Upload the certified shareholder register. It should show each shareholder&apos;s name,
            their shareholding, and an official stamp or signature.
          </p>
          <FileUpload
            value={certifiedListFile}
            onChange={(file) => handleSectionFileChange('certified_list', file)}
            onDelete={() => {
              setFileDeleteTarget('certified_list');
              setShowFileDeleteModal(true);
            }}
            sampleTitle={{ normal: 'Certified List of', cursive: 'Shareholders' }}
            sampleImage={KYB_SAMPLE_DOCS.CERTIFIED_SHAREHOLDER_LIST}
            maxSizeMB={5}
            error={fieldErrors.certified_list_file}
            disabled={readOnly}
          />
        </div>
      </div>

      {/* ── Ultimate Beneficial Owners ── */}
      <div className="corp-step-form__section-card">
        <h3 className="corp-step-form__section-title">Ultimate Beneficial Owners (UBO)</h3>
        {!readOnly && (
          <button
            type="button"
            className="corp-step-form__add-btn"
            onClick={() => openPanel('ubo')}>
            + Add UBO
          </button>
        )}

        {ubos.length > 0 && (
          <div className="ownership-person-list">
            {ubos.map((u) => (
              <PersonRow
                key={u.id}
                name={u.name}
                readOnly={readOnly}
                onEdit={() => openPanel('ubo', u)}
                onDelete={() => {
                  setDeleteTarget({ type: 'ubo', id: u.id });
                  setShowDeleteModal(true);
                }}
              />
            ))}
          </div>
        )}

        {fieldErrors.ubos && <span className="corp-step-form__error">{fieldErrors.ubos}</span>}
      </div>

      {!readOnly && (
        <Button type="submit" isFull isLoading={isSubmitting} disabled={!isFormValid}>
          Save and Continue
        </Button>
      )}

      {/* ── Side panels ── */}
      {panel?.type === 'director' && (
        <DirectorPanel entry={panel.entry} onSave={handlePanelSave} onClose={closePanel} />
      )}
      {panel?.type === 'shareholder' && (
        <ShareholderPanel entry={panel.entry} onSave={handlePanelSave} onClose={closePanel} />
      )}
      {panel?.type === 'ubo' && (
        <UBOPanel
          entry={panel.entry}
          onSave={handlePanelSave}
          onClose={closePanel}
          onIdUploaded={() =>
            track(ANALYTICS_EVENTS.KYB_UBO_ID_UPLOADED, {
              kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI
            })
          }
        />
      )}

      {/* ── Remove person modal ── */}
      {showDeleteModal && (
        <Modal
          Icon={WarningStaticIcon}
          header={{ name: 'Remove', cursive: 'Person' }}
          onModalClose={() => {
            setShowDeleteModal(false);
            setDeleteTarget(null);
          }}>
          <p className="modal__text">Are you sure you want to remove this person?</p>
          <Button
            isFull
            variant="outline"
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteTarget(null);
            }}>
            Cancel
          </Button>
          <Button isFull onClick={onDeletePerson}>
            Yes, continue
          </Button>
        </Modal>
      )}

      {/* ── Delete section file modal ── */}
      {showFileDeleteModal && (
        <Modal
          Icon={WarningStaticIcon}
          header={{ name: 'Delete', cursive: 'Document' }}
          onModalClose={() => {
            setShowFileDeleteModal(false);
            setFileDeleteTarget(null);
          }}>
          <p className="modal__text">Are you sure you want to delete this document?</p>
          <Button
            isFull
            variant="outline"
            onClick={() => {
              setShowFileDeleteModal(false);
              setFileDeleteTarget(null);
            }}>
            Cancel
          </Button>
          <Button isFull onClick={onDeleteFile}>
            Yes, continue
          </Button>
        </Modal>
      )}
    </form>
  );
};

OwnershipStructure.propTypes = {
  onComplete: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  onSaveDraft: PropTypes.func,
  readOnly: PropTypes.bool
};

export { OwnershipStructure };
